// src/components/NotificationPrompt.jsx
// Small banner asking users to allow push notifications for new design updates.

import { useState, useEffect, useCallback, useRef } from "react";
import { B, fonts } from "./UI";
import { requestFcmToken, registerFcmSw } from "../firebase/config";
import { saveFcmToken } from "../firebase/fcmTokens";

const STORAGE_KEY = "nilam-notif-prompt-dismissed";
const TOKEN_CACHE_KEY = "nilam-fcm-token";

export default function NotificationPrompt() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "true"; }
    catch { return false; }
  });
  const [granted, setGranted] = useState(() => {
    return "Notification" in window && Notification.permission === "granted";
  });
  // Prevent duplicate saves while async operations are in flight
  const savingRef = useRef(false);

  // Check if we already have a cached token (means we already registered)
  const hasCachedToken = useRef(() => {
    try { return !!localStorage.getItem(TOKEN_CACHE_KEY); }
    catch { return false; }
  });

  // Show banner after 5 seconds, only if not dismissed/granted/already registered
  useEffect(() => {
    if (dismissed || granted || hasCachedToken.current()) return;
    const timer = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(timer);
  }, [dismissed, granted]);

  const handleAllow = useCallback(async () => {
    // Immediately hide the dialog for instant visual feedback
    setVisible(false);

    // Prevent concurrent save attempts
    if (savingRef.current) return;
    savingRef.current = true;

    try {
      await registerFcmSw();
      const token = await requestFcmToken();
      console.log("[NotificationPrompt] FCM token:", token);
      if (token) {
        // Cache token locally to avoid re-registration on next page load
        try { localStorage.setItem(TOKEN_CACHE_KEY, token); } catch {}
        await saveFcmToken(token);
        setGranted(true);
      }
    } catch (err) {
      console.error("[NotificationPrompt] Error:", err);
    } finally {
      savingRef.current = false;
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setDismissed(true);
    try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
  }, []);

  if (!visible || granted || dismissed || hasCachedToken.current()) return null;

  return (
    <div style={{
      position: "fixed", bottom: 16, left: 16, right: 16, maxWidth: 400,
      zIndex: 9999, background: B.white, border: `1px solid ${B.borderLight}`,
      borderRadius: 14, boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
      padding: "16px 18px", fontFamily: fonts.body,
    }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <span style={{ fontSize: 24, lineHeight: 1 }}>🔔</span>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: B.text }}>
            Get notified about new designs!
          </p>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: B.textLight, lineHeight: 1.5 }}>
            Allow notifications to know when we add fresh designs to our collection.
          </p>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button type="button" onClick={handleAllow} style={{
              padding: "8px 16px", fontSize: 13, fontWeight: 700,
              background: B.orange, color: B.white, border: "none",
              borderRadius: 8, cursor: "pointer", fontFamily: fonts.body,
            }}>
              Allow Notifications
            </button>
            <button type="button" onClick={handleDismiss} style={{
              padding: "8px 14px", fontSize: 13, fontWeight: 600,
              background: "transparent", color: B.textMid, border: `1px solid ${B.borderLight}`,
              borderRadius: 8, cursor: "pointer", fontFamily: fonts.body,
            }}>
              Not now
            </button>
          </div>
        </div>
        <button type="button" onClick={handleDismiss} aria-label="Close" style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 18, color: B.textLight, padding: 0, lineHeight: 1,
        }}>
          ✕
        </button>
      </div>
    </div>
  );
}